import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { useContactListStore } from "@/store/contactListStore";
import { useContactStore } from "@/store/contactStore";
import { useAuthStore } from "@/store/authStore";
import type { Contact } from "@/types/contacts";

function displayName(c: Contact) {
  return c.fullName?.trim() || c.primaryEmail || c.primaryPhone || c.id.slice(0, 8);
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: diffDays > 365 ? "numeric" : undefined });
}

const PAGE_SIZE = 20;

interface AddContactsModalProps {
  accountId: string;
  listId: string;
  onClose: () => void;
}

function AddContactsModal({ accountId, listId, onClose }: AddContactsModalProps) {
  const { addContacts, isSaving, error: listError } = useContactListStore();
  const { contacts, isLoading: contactsLoading, fetchContacts } = useContactStore();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchContacts(accountId, { pageSize: 50, search: search || undefined });
  }, [accountId, search, fetchContacts]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    try {
      await addContacts(accountId, listId, Array.from(selected));
      onClose();
    } catch {
      // error from store
    }
  };

  return (
    <div className="space-y-4">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search contacts…"
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
      />
      {listError && <p className="text-sm text-destructive">{listError}</p>}
      <div className="max-h-64 overflow-y-auto space-y-1 rounded-md border border-border">
        {contactsLoading && (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary mr-2" />
            Loading…
          </div>
        )}
        {!contactsLoading && contacts.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">No contacts found.</p>
        )}
        {contacts.map((c) => (
          <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-accent cursor-pointer">
            <input
              type="checkbox"
              checked={selected.has(c.id)}
              onChange={() => toggleSelect(c.id)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <div>
              <span className="text-sm font-medium text-foreground">{displayName(c)}</span>
              {c.primaryEmail && <span className="ml-2 text-xs text-muted-foreground">{c.primaryEmail}</span>}
            </div>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between pt-2">
        <span className="text-xs text-muted-foreground">{selected.size} selected</span>
        <div className="flex gap-2">
          <button onClick={onClose} className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors">Cancel</button>
          <Button onClick={handleAdd} disabled={isSaving || selected.size === 0}>{isSaving ? "Adding…" : `Add ${selected.size > 0 ? selected.size : ""} contacts`}</Button>
        </div>
      </div>
    </div>
  );
}

export default function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accountId = user?.currentAccountId ?? user?.ownedAccountId ?? "";

  const { currentList, listContacts, listContactsTotalCount, isLoading, isSaving, error, fetchList, fetchListContacts, updateList, removeContacts } = useContactListStore();
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Edit inline
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    if (accountId && listId) {
      fetchList(accountId, listId);
    }
  }, [accountId, listId, fetchList]);

  useEffect(() => {
    if (accountId && listId) {
      fetchListContacts(accountId, listId, { page, pageSize: PAGE_SIZE });
    }
  }, [accountId, listId, page, fetchListContacts]);

  const [nameSource, setNameSource] = useState<typeof currentList>(null);
  if (currentList !== nameSource) {
    setNameSource(currentList);
    if (currentList) setNameDraft(currentList.name);
  }

  const totalPages = Math.max(1, Math.ceil(listContactsTotalCount / PAGE_SIZE));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    if (!accountId || !listId || selectedIds.size === 0) return;
    await removeContacts(accountId, listId, Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleSaveName = async () => {
    if (!accountId || !listId || !nameDraft.trim()) return;
    await updateList(accountId, listId, { name: nameDraft.trim() });
    setEditingName(false);
  };

  if (isLoading && !currentList) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center py-24">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading…
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (!isLoading && !currentList) {
    return (
      <ProtectedRoute>
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">{error ?? "List not found."}</p>
          <button onClick={() => navigate("/audience/lists")} className="mt-4 text-sm text-primary hover:underline">← Back to lists</button>
        </div>
      </ProtectedRoute>
    );
  }

  const list = currentList!;
  const isStatic = list.listType === "Static";

  return (
    <ProtectedRoute>
      <div className="lw-list-detail container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate("/audience/lists")} className="mb-6 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Lists
          </button>

          {/* Header */}
          <div className="lw-list-header mb-8">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0 mr-4">
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className="text-2xl font-bold bg-transparent border-b border-primary focus:outline-none text-foreground w-full max-w-sm"
                      autoFocus
                    />
                    <button onClick={handleSaveName} disabled={isSaving} className="text-sm text-primary hover:underline">{isSaving ? "Saving…" : "Save"}</button>
                    <button onClick={() => setEditingName(false)} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group">
                    <h1 className="text-2xl font-bold text-foreground">{list.name}</h1>
                    <button onClick={() => setEditingName(true)} className="opacity-0 group-hover:opacity-100 rounded p-0.5 text-muted-foreground hover:text-foreground transition-all">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                )}
                {list.description && <p className="text-sm text-muted-foreground mt-1">{list.description}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${list.listType === "Static" ? "bg-muted text-muted-foreground" : "bg-blue-500/10 text-blue-500"}`}>
                  {list.listType}
                </span>
                <span className="text-sm text-muted-foreground">{list.contactCount.toLocaleString()} contacts</span>
              </div>
            </div>
          </div>

          {/* Dynamic list note */}
          {!isStatic && (
            <div className="mb-6 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 text-sm text-blue-600">
              Dynamic list membership is based on filter criteria and is managed automatically.
            </div>
          )}

          {/* Contacts table */}
          <div className="lw-list-contacts-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-foreground">Contacts</h2>
              <div className="flex items-center gap-2">
                {isStatic && selectedIds.size > 0 && (
                  <button
                    onClick={handleRemoveSelected}
                    disabled={isSaving}
                    className="rounded-md border border-destructive/30 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    {isSaving ? "Removing…" : `Remove ${selectedIds.size} selected`}
                  </button>
                )}
                {isStatic && (
                  <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add contacts
                  </Button>
                )}
              </div>
            </div>

            {error && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-4"><p className="text-sm text-destructive">{error}</p></div>}

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                  Loading…
                </div>
              </div>
            )}

            {!isLoading && listContacts.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-12 text-center">
                <p className="text-sm text-muted-foreground">No contacts in this list yet.</p>
                {isStatic && (
                  <button onClick={() => setShowAddModal(true)} className="mt-3 text-sm text-primary hover:underline">Add contacts</button>
                )}
              </div>
            )}

            {!isLoading && listContacts.length > 0 && (
              <>
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        {isStatic && <th className="w-10 px-4 py-3" />}
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</th>
                        <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                        <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Company</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {listContacts.map((c) => (
                        <tr key={c.id} className="bg-card hover:bg-accent/30 transition-colors">
                          {isStatic && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(c.id)}
                                onChange={() => toggleSelect(c.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="rounded border-border text-primary focus:ring-primary"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 font-medium text-foreground">{displayName(c)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{c.primaryEmail ?? <span className="italic text-muted-foreground/40">—</span>}</td>
                          <td className="hidden sm:table-cell px-4 py-3">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              c.status === "Active" ? "bg-green-500/10 text-green-600" :
                              c.status === "Unsubscribed" ? "bg-yellow-500/10 text-yellow-600" :
                              "bg-muted text-muted-foreground"
                            }`}>{c.status}</span>
                          </td>
                          <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">{c.company ?? <span className="italic text-muted-foreground/40">—</span>}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => navigate(`/audience/contacts/${c.id}`)}
                              className="text-xs text-primary hover:underline"
                            >
                              View →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Page {page} of {totalPages} ({listContactsTotalCount.toLocaleString()} total)</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="rounded-md border border-border bg-background px-3 py-2 hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="rounded-md border border-border bg-background px-3 py-2 hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showAddModal && listId && (
        <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Contacts to List" size="md">
          <AddContactsModal accountId={accountId} listId={listId} onClose={() => setShowAddModal(false)} />
        </Modal>
      )}
    </ProtectedRoute>
  );
}
